import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { ChevronRight, ChevronLeft, Check, User, Wrench, ClipboardList, PackageCheck, Minus, Plus } from 'lucide-react'
import { serviceAuftraege as serviceApi, kunden as kundenApi, geraete as geraeteApi, ersatzteile as ersatzteileApi } from '../../lib/api'
import { TopBar } from '../../components/layout/TopBar'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Select } from '../../components/ui/Select'
import { Input } from '../../components/ui/Input'
import { Textarea } from '../../components/ui/Textarea'
import { cn } from '../../lib/utils'
import type { ServiceErsatzteilPosition } from '../../types'

const STUNDENSATZ = 110 // CHF pro Stunde

const STEPS = [
  { label: 'Gerät', icon: User },
  { label: 'Problem', icon: Wrench },
  { label: 'Reparatur', icon: ClipboardList },
  { label: 'Abschluss', icon: PackageCheck },
]

interface FormState {
  kunde_id: string
  geraet_id: string
  beschreibung: string
  garantie: boolean
  reparatur_log: string
  was_ersetzt: string
  stunden: string
  minuten: string
  ersatzteile: ServiceErsatzteilPosition[]
  kosten_manuell: string        // optionale manuelle Anpassung
  verrechnet: boolean
  bexio_rechnung_id: string
  ruecksendung_datum: string
  ruecksendung_tracking: string
  ruecksendung_notizen: string
}

const INITIAL: FormState = {
  kunde_id: '', geraet_id: '',
  beschreibung: '', garantie: false,
  reparatur_log: '', was_ersetzt: '', stunden: '', minuten: '',
  ersatzteile: [],
  kosten_manuell: '',
  verrechnet: false, bexio_rechnung_id: '',
  ruecksendung_datum: '', ruecksendung_tracking: '', ruecksendung_notizen: '',
}

export function ServiceFormPage() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [step, setStep] = useState(0)
  const [form, setForm] = useState<FormState>(INITIAL)
  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setForm((f) => ({ ...f, [k]: v }))

  const { data: kundenList } = useQuery({ queryKey: ['kunden'], queryFn: () => kundenApi.list() })
  const { data: geraeteList } = useQuery({
    queryKey: ['geraete', form.kunde_id],
    queryFn: () => geraeteApi.list(form.kunde_id),
    enabled: !!form.kunde_id,
  })
  const { data: ersatzteileList } = useQuery({ queryKey: ['ersatzteile'], queryFn: () => ersatzteileApi.list() })

  // ─── Kostenberechnung ───────────────────────────────────
  const zeitMinutenTotal = (parseInt(form.stunden || '0') * 60) + parseInt(form.minuten || '0')
  const zeitKosten = parseFloat(((zeitMinutenTotal / 60) * STUNDENSATZ).toFixed(2))
  const ersatzteileTotal = parseFloat(form.ersatzteile.reduce((s, e) => s + e.menge * e.einzel_preis, 0).toFixed(2))
  const autoTotal = parseFloat((zeitKosten + ersatzteileTotal).toFixed(2))
  const finalKosten = form.kosten_manuell !== '' ? parseFloat(form.kosten_manuell || '0') : autoTotal

  const mutation = useMutation({
    mutationFn: () => serviceApi.create({
      kunde_id: form.kunde_id,
      geraet_id: form.geraet_id,
      beschreibung: form.beschreibung,
      garantie: form.garantie ? 1 : 0,
      status: 'offen',
      reparatur_log: form.reparatur_log || undefined,
      was_ersetzt: form.was_ersetzt || undefined,
      ersatzteile: JSON.stringify(form.ersatzteile),
      zeit_minuten: zeitMinutenTotal,
      kosten: finalKosten,
      verrechnet: form.verrechnet ? 1 : 0,
      bexio_rechnung_id: form.bexio_rechnung_id || undefined,
      ruecksendung_datum: form.ruecksendung_datum || undefined,
      ruecksendung_tracking: form.ruecksendung_tracking || undefined,
      ruecksendung_notizen: form.ruecksendung_notizen || undefined,
    }),
    onSuccess: (s) => {
      qc.invalidateQueries({ queryKey: ['service'] })
      navigate(`/service/${s.id}`)
    },
  })

  const canNext = () => {
    if (step === 0) return !!form.kunde_id && !!form.geraet_id
    if (step === 1) return !!form.beschreibung.trim()
    return true
  }

  const addErsatzteil = (id: string) => {
    const teil = ersatzteileList?.find((e) => e.id === id)
    if (!teil) return
    const exists = form.ersatzteile.find((e) => e.id === id)
    if (exists) {
      set('ersatzteile', form.ersatzteile.map((e) => e.id === id ? { ...e, menge: e.menge + 1 } : e))
    } else {
      set('ersatzteile', [...form.ersatzteile, { id, bezeichnung: teil.bezeichnung, menge: 1, einzel_preis: teil.preis ?? 0 }])
    }
    set('kosten_manuell', '') // reset manual override when parts change
  }

  const changeErsatzteilMenge = (id: string, delta: number) => {
    const updated = form.ersatzteile
      .map((e) => e.id === id ? { ...e, menge: e.menge + delta } : e)
      .filter((e) => e.menge > 0)
    set('ersatzteile', updated)
    set('kosten_manuell', '')
  }

  return (
    <>
      <TopBar title="Neuer Service-Auftrag" backTo="/service" />

      {/* Step Indicator */}
      <div className="px-4 pt-3 pb-1">
        <div className="flex items-center">
          {STEPS.map((s, i) => (
            <div key={i} className="flex items-center flex-1 last:flex-none">
              <button
                onClick={() => i < step && setStep(i)}
                className={cn(
                  'flex items-center gap-1.5 text-xs font-medium transition-colors',
                  i === step ? 'text-blue-600' : i < step ? 'text-green-600 cursor-pointer' : 'text-gray-300'
                )}
              >
                <div className={cn(
                  'w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all',
                  i === step ? 'border-blue-600 bg-blue-50 text-blue-600' :
                  i < step ? 'border-green-500 bg-green-500 text-white' :
                  'border-gray-200 bg-white text-gray-300'
                )}>
                  {i < step ? <Check size={12} strokeWidth={3} /> : i + 1}
                </div>
                <span className="hidden sm:inline">{s.label}</span>
              </button>
              {i < STEPS.length - 1 && (
                <div className={cn('flex-1 h-0.5 mx-1', i < step ? 'bg-green-400' : 'bg-gray-200')} />
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="px-4 py-3 pb-28 space-y-4">

        {/* ─── STEP 1: Gerät ─── */}
        {step === 0 && (
          <Card>
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <User size={16} className="text-blue-600" /> Kunde & Gerät auswählen
            </h3>
            <div className="space-y-4">
              <Select
                label="Kunde" required
                value={form.kunde_id}
                placeholder="Kunde wählen..."
                options={(kundenList ?? []).map((k) => ({ value: k.id, label: k.firma ?? k.name }))}
                onChange={(e) => { set('kunde_id', e.target.value); set('geraet_id', '') }}
              />
              {form.kunde_id && (
                <Select
                  label="Gerät" required
                  value={form.geraet_id}
                  placeholder="Gerät wählen..."
                  options={(geraeteList ?? []).map((g) => ({
                    value: g.id,
                    label: `${g.modell}${g.seriennummer ? ` · ${g.seriennummer}` : ''}${g.standort ? ` (${g.standort})` : ''}`
                  }))}
                  onChange={(e) => set('geraet_id', e.target.value)}
                />
              )}
              {form.kunde_id && (geraeteList ?? []).length === 0 && (
                <p className="text-sm text-amber-600 bg-amber-50 rounded-xl p-3">
                  Dieser Kunde hat noch keine Geräte im Inventar.
                </p>
              )}
            </div>
          </Card>
        )}

        {/* ─── STEP 2: Problem ─── */}
        {step === 1 && (
          <Card>
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Wrench size={16} className="text-blue-600" /> Fehlermeldung & Abrechnung
            </h3>
            <div className="space-y-4">
              <Textarea
                label="Fehlerbeschreibung / Defekt" required
                placeholder="Was ist das Problem? Was meldet der Kunde?"
                value={form.beschreibung}
                onChange={(e) => set('beschreibung', e.target.value)}
                rows={4}
              />
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Abrechnung</p>
                <div className="grid grid-cols-2 gap-2">
                  <button type="button" onClick={() => set('garantie', false)}
                    className={cn('py-3 px-4 rounded-xl border-2 text-sm font-medium transition-all',
                      !form.garantie ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-gray-200 bg-white text-gray-600'
                    )}>
                    💳 Bezahlt
                  </button>
                  <button type="button" onClick={() => set('garantie', true)}
                    className={cn('py-3 px-4 rounded-xl border-2 text-sm font-medium transition-all',
                      form.garantie ? 'border-amber-500 bg-amber-50 text-amber-700' : 'border-gray-200 bg-white text-gray-600'
                    )}>
                    🛡️ Garantie
                  </button>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* ─── STEP 3: Reparatur ─── */}
        {step === 2 && (
          <Card>
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <ClipboardList size={16} className="text-blue-600" /> Reparatur-Details
            </h3>
            <div className="space-y-4">
              <Textarea
                label="Reparatur-Log"
                placeholder="Was wurde gemacht? Schritt für Schritt..."
                value={form.reparatur_log}
                onChange={(e) => set('reparatur_log', e.target.value)}
                rows={4}
              />
              <Textarea
                label="Was wurde ersetzt"
                placeholder="Welche Teile wurden ausgetauscht?"
                value={form.was_ersetzt}
                onChange={(e) => set('was_ersetzt', e.target.value)}
                rows={2}
              />
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Benötigte Zeit</p>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <Input
                      label="Stunden" type="number" min="0" placeholder="0"
                      value={form.stunden}
                      onChange={(e) => { set('stunden', e.target.value); set('kosten_manuell', '') }}
                    />
                  </div>
                  <div className="flex-1">
                    <Input
                      label="Minuten" type="number" min="0" max="59" placeholder="0"
                      value={form.minuten}
                      onChange={(e) => { set('minuten', e.target.value); set('kosten_manuell', '') }}
                    />
                  </div>
                </div>
                {zeitMinutenTotal > 0 && (
                  <p className="text-xs text-gray-500 mt-1.5">
                    = CHF {zeitKosten.toFixed(2)} ({STUNDENSATZ}.– / Stunde)
                  </p>
                )}
              </div>
            </div>
          </Card>
        )}

        {/* ─── STEP 4: Kosten & Abschluss ─── */}
        {step === 3 && (
          <div className="space-y-4">

            {/* Ersatzteile */}
            <Card>
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <PackageCheck size={16} className="text-blue-600" /> Ersatzteile
              </h3>

              {form.ersatzteile.length > 0 && (
                <div className="space-y-2 mb-3">
                  {form.ersatzteile.map((teil) => (
                    <div key={teil.id} className="flex items-center gap-3 bg-gray-50 rounded-xl px-3 py-2.5">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{teil.bezeichnung}</p>
                        <p className="text-xs text-gray-500">CHF {teil.einzel_preis.toFixed(2)} / Stk.</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <button type="button" onClick={() => changeErsatzteilMenge(teil.id, -1)}
                          className="w-7 h-7 rounded-lg bg-white border border-gray-200 text-gray-600 flex items-center justify-center hover:border-red-300 hover:text-red-600 transition-colors">
                          <Minus size={12} strokeWidth={2.5} />
                        </button>
                        <span className="text-sm font-bold text-gray-900 w-5 text-center">{teil.menge}</span>
                        <button type="button" onClick={() => changeErsatzteilMenge(teil.id, 1)}
                          className="w-7 h-7 rounded-lg bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 transition-colors">
                          <Plus size={12} strokeWidth={2.5} />
                        </button>
                        <span className="text-sm font-semibold text-gray-900 w-16 text-right">
                          CHF {(teil.menge * teil.einzel_preis).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <Select
                label={form.ersatzteile.length > 0 ? 'Weiteres Teil hinzufügen' : 'Teil hinzufügen'}
                value=""
                placeholder="Ersatzteil auswählen..."
                options={(ersatzteileList ?? [])
                  .filter((e) => !form.ersatzteile.find((f) => f.id === e.id))
                  .map((e) => ({
                    value: e.id,
                    label: `${e.bezeichnung} (Lager: ${e.lagerbestand} ${e.einheit}${e.preis ? ` · CHF ${e.preis}` : ''})`
                  }))}
                onChange={(e) => { if (e.target.value) addErsatzteil(e.target.value) }}
              />
              <p className="text-xs text-gray-400 mt-1.5">Bexio-Artikel nach der Anbindung verfügbar.</p>
            </Card>

            {/* Kostenübersicht */}
            <Card>
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <PackageCheck size={16} className="text-blue-600" /> Kostenübersicht
              </h3>

              <div className="space-y-2 mb-3">
                {/* Arbeitszeit */}
                <div className="flex justify-between items-center py-1.5">
                  <div>
                    <p className="text-sm text-gray-700">Arbeitszeit</p>
                    <p className="text-xs text-gray-400">
                      {zeitMinutenTotal > 0
                        ? `${Math.floor(zeitMinutenTotal / 60)}h ${zeitMinutenTotal % 60 > 0 ? (zeitMinutenTotal % 60) + 'min' : ''} × CHF ${STUNDENSATZ}.–`
                        : `CHF ${STUNDENSATZ}.– / Stunde`}
                    </p>
                  </div>
                  <span className={cn('text-sm font-semibold', zeitKosten > 0 ? 'text-gray-900' : 'text-gray-300')}>
                    CHF {zeitKosten.toFixed(2)}
                  </span>
                </div>

                {/* Ersatzteile Zeilen */}
                {form.ersatzteile.map((teil) => (
                  <div key={teil.id} className="flex justify-between items-center py-1 border-t border-gray-50">
                    <div>
                      <p className="text-sm text-gray-700">{teil.bezeichnung}</p>
                      <p className="text-xs text-gray-400">CHF {teil.einzel_preis.toFixed(2)} × {teil.menge}</p>
                    </div>
                    <span className="text-sm font-semibold text-gray-900">CHF {(teil.menge * teil.einzel_preis).toFixed(2)}</span>
                  </div>
                ))}

                {/* Total */}
                <div className="flex justify-between items-center py-2 border-t-2 border-gray-200 mt-1">
                  <p className="font-bold text-gray-900">Total</p>
                  <p className="text-lg font-bold text-gray-900">CHF {autoTotal.toFixed(2)}</p>
                </div>
              </div>

              {/* Manuelle Anpassung */}
              <div className="pt-2 border-t border-gray-100">
                <Input
                  label="Manuelle Anpassung (optional)"
                  type="number"
                  min="0"
                  step="0.05"
                  placeholder={`${autoTotal.toFixed(2)} (berechnet)`}
                  value={form.kosten_manuell}
                  onChange={(e) => set('kosten_manuell', e.target.value)}
                />
                {form.kosten_manuell !== '' && parseFloat(form.kosten_manuell) !== autoTotal && (
                  <p className="text-xs text-amber-600 mt-1">
                    Abweichung vom berechneten Total (CHF {autoTotal.toFixed(2)})
                  </p>
                )}
              </div>
            </Card>

            {/* Verrechnung */}
            <Card>
              <h3 className="font-semibold text-gray-900 mb-3">Verrechnung</h3>
              <div className="grid grid-cols-2 gap-2 mb-4">
                <button type="button" onClick={() => set('verrechnet', false)}
                  className={cn('py-3 px-4 rounded-xl border-2 text-sm font-medium transition-all',
                    !form.verrechnet ? 'border-red-400 bg-red-50 text-red-700' : 'border-gray-200 bg-white text-gray-600'
                  )}>Noch offen</button>
                <button type="button" onClick={() => set('verrechnet', true)}
                  className={cn('py-3 px-4 rounded-xl border-2 text-sm font-medium transition-all',
                    form.verrechnet ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-200 bg-white text-gray-600'
                  )}>Verrechnet ✓</button>
              </div>
              <Input
                label="Bexio Rechnungs-Nr. (optional)"
                placeholder="Wird bei Bexio-Anbindung automatisch befüllt"
                value={form.bexio_rechnung_id}
                onChange={(e) => set('bexio_rechnung_id', e.target.value)}
              />
            </Card>

            {/* Rücksendung */}
            <Card>
              <h3 className="font-semibold text-gray-900 mb-4">Rücksendung an Kunden</h3>
              <div className="space-y-4">
                <Input label="Versanddatum" type="date"
                  value={form.ruecksendung_datum}
                  onChange={(e) => set('ruecksendung_datum', e.target.value)}
                />
                <Input label="Tracking-Nummer" placeholder="Post CH, DHL, ..."
                  value={form.ruecksendung_tracking}
                  onChange={(e) => set('ruecksendung_tracking', e.target.value)}
                />
                <Textarea label="Notizen zur Rücksendung"
                  placeholder="z.B. Abgabe beim Kunden vor Ort..."
                  value={form.ruecksendung_notizen}
                  onChange={(e) => set('ruecksendung_notizen', e.target.value)}
                  rows={2}
                />
              </div>
            </Card>

            {/* Abschluss-Zusammenfassung */}
            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4">
              <p className="text-sm font-semibold text-blue-900 mb-2">Zusammenfassung</p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                <span className="text-blue-500">Abrechnung</span>
                <span className="font-medium text-blue-900">{form.garantie ? '🛡️ Garantie' : '💳 Bezahlt'}</span>
                {zeitMinutenTotal > 0 && (
                  <>
                    <span className="text-blue-500">Arbeitszeit</span>
                    <span className="font-medium text-blue-900">{form.stunden || '0'}h {form.minuten || '0'}min</span>
                  </>
                )}
                {form.ersatzteile.length > 0 && (
                  <>
                    <span className="text-blue-500">Ersatzteile</span>
                    <span className="font-medium text-blue-900">{form.ersatzteile.length} Position(en)</span>
                  </>
                )}
                <span className="text-blue-500 font-semibold">Rechnungsbetrag</span>
                <span className="font-bold text-blue-900">CHF {finalKosten.toFixed(2)}</span>
              </div>
            </div>
          </div>
        )}

        {mutation.error && (
          <p className="text-sm text-red-600 text-center">{mutation.error.message}</p>
        )}
      </div>

      {/* Fixed Bottom Navigation */}
      <div className="fixed bottom-[env(safe-area-inset-bottom,0px)] left-0 right-0 z-50 bg-white border-t border-gray-200 px-4 py-3 pb-[calc(env(safe-area-inset-bottom,0px)+60px)] flex gap-3">
        {step > 0 && (
          <Button variant="outline" fullWidth icon={<ChevronLeft size={16} />} onClick={() => setStep(step - 1)}>
            Zurück
          </Button>
        )}
        {step < STEPS.length - 1 ? (
          <Button fullWidth disabled={!canNext()} icon={<ChevronRight size={16} />} onClick={() => setStep(step + 1)}>
            Weiter
          </Button>
        ) : (
          <Button fullWidth loading={mutation.isPending} icon={<Check size={16} />} onClick={() => mutation.mutate()}>
            Auftrag speichern
          </Button>
        )}
      </div>
    </>
  )
}
